import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { NavigationItemWithState } from '@/entities/navigation';
import { resolveIcon } from '@/entities/navigation';

interface NavigationItemProps {
  item: NavigationItemWithState;
  isCollapsed: boolean;
  onToggle: (code: string) => void;
  level?: number;
}

export const NavigationItem = ({ item, isCollapsed, onToggle, level = 0 }: NavigationItemProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const Icon = resolveIcon(item.icon);
  const hasSubItems = item.subItems && item.subItems.length > 0;

  const handleClick = () => {
    if (hasSubItems) {
      onToggle(item.code);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const isCurrentPath = item.path === location.pathname;

  return (
    <>
      <button
        onClick={handleClick}
        className={cn(
          'w-full flex items-center gap-3 px-2 py-2 rounded-md transition-colors text-sm',
          'hover:bg-accent hover:text-accent-foreground',
          (item.isActive || isCurrentPath) && 'bg-accent text-accent-foreground font-medium',
          !item.isActive && !isCurrentPath && 'text-foreground/80',
          level > 0 && 'ml-4'
        )}>
        <Icon className="w-4 h-4 shrink-0" />
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left truncate">{item.label}</span>
            {item.badge && (
              <span className="bg-destructive text-destructive-foreground px-1.5 py-0.5 text-xs rounded-full">
                {item.badge}
              </span>
            )}
            {hasSubItems && (item.isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
          </>
        )}
      </button>

      {!isCollapsed && hasSubItems && item.isExpanded && (
        <ul className="mt-1 ml-4 pl-2 border-l space-y-1">
          {item.subItems?.map((subItem) => (
            <li key={subItem.code}>
              <NavigationItem
                item={subItem as NavigationItemWithState}
                isCollapsed={isCollapsed}
                onToggle={onToggle}
                level={level + 1}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
